import React from 'react';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService: React.FC<{ onBack: () => void }> = ({ onBack }) => (
    <div className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-slate-400 mb-8">Last updated: March 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-sm leading-relaxed text-slate-700">
            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">1. About EstateClarity</h2>
                <p>EstateClarity provides an online tool that helps users estimate UK inheritance tax (IHT) liability and assess whether probate is likely to be required. The tool produces estimates based on the information you provide and publicly available HMRC guidance.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">2. Not professional advice</h2>
                <p><strong>EstateClarity is not a substitute for professional legal, tax, or financial advice.</strong> The estimates, assessments, and guidance provided by this tool are for informational purposes only. You should always consult a qualified solicitor, chartered tax adviser, or accountant before making decisions about an estate, filing tax returns, or applying for probate.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">3. AI-powered research</h2>
                <p>The research assistant feature uses AI (Google Gemini) to answer questions about inheritance tax. AI-generated responses may contain errors, omissions, or misinterpretations of HMRC guidance. You must not rely solely on AI-generated content for legal or financial decisions.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">4. Accuracy and limitations</h2>
                <p>We make reasonable efforts to ensure the calculation engine reflects current HMRC rules, but we cannot guarantee accuracy. Tax rules change, and every estate has unique circumstances. The tool may not cover all scenarios, particularly estates involving trusts, foreign assets, or complex ownership structures.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">5. User accounts</h2>
                <p>You are responsible for maintaining the security of your account credentials. You must not share your account or allow others to access it. We reserve the right to suspend accounts that violate these terms.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">6. Payments and refunds</h2>
                <p>Payments for unlocked reports are processed securely via Stripe. Once a report has been unlocked and viewed, it is non-refundable. If you experience a technical issue preventing access to a paid report, contact <a href="mailto:support@estateclarity.co.uk" className="text-indigo-600 hover:underline">support@estateclarity.co.uk</a> and we will resolve it.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">7. Limitation of liability</h2>
                <p>To the fullest extent permitted by law, EstateClarity shall not be liable for any indirect, incidental, or consequential damages arising from your use of the tool, including any losses resulting from reliance on the estimates or guidance provided. Our total liability is limited to the amount you paid for the service.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">8. Intellectual property</h2>
                <p>The EstateClarity software, design, and content are protected by copyright. You may not copy, modify, or distribute any part of the service without written permission. Reports generated for your estates are yours to use and share as you wish.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">9. Governing law</h2>
                <p>These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
            </section>

            <section>
                <h2 className="text-lg font-semibold text-slate-800 mt-6 mb-2">10. Changes to these terms</h2>
                <p>We may update these terms from time to time. Continued use of the service after changes are posted constitutes acceptance. Material changes will be communicated via email to registered users.</p>
            </section>
        </div>
    </div>
);
